import React from "react";
import { useParams } from "react-router-dom";
import { Reveal } from "@/components/ui/Reveal";
import { StatCounter } from "@/components/ui/StatCounter";
import { RichText, SectionHeading } from "@/components/ui/RichText";
import { ServiceCard } from "@/components/sections/ServiceCard";
import { pick } from "@/lib/helpers";
import { mediaUrl } from "@/lib/api";

/**
 * Maps a content-block `type` to a fixed, design-safe React component.
 * The admin can only edit text/images/order/visibility — never structure.
 */
export function ServiceBlock({ block, related = [] }) {
  const { lang = "en" } = useParams();
  if (block.visible === false) return null;
  const heading = pick({ en: block.headingEn, fr: block.headingFr }, lang);
  const content = pick({ en: block.contentEn, fr: block.contentFr }, lang);
  const items = block.items || [];

  switch (block.type) {
    case "richText":
      return (
        <Block heading={heading}>
          <RichText html={content} />
        </Block>
      );

    case "imageText":
      return (
        <div className="grid lg:grid-cols-2 gap-10 lg:gap-16 items-center">
          <Reveal>
            <div className="relative">
              <div className="absolute -inset-3 border border-accent/20" />
              <img src={mediaUrl(block.imageUrl, { w: 1200, fmt: "webp", q: 78 })} alt={pick({ en: block.imageAltEn, fr: block.imageAltFr }, lang)} loading="lazy" decoding="async" className="relative w-full aspect-[4/3] object-cover" />
            </div>
          </Reveal>
          <Reveal delay={0.1}>
            {heading ? <h3 className="h-display text-fg text-3xl mb-4">{heading}</h3> : null}
            <RichText html={content} />
          </Reveal>
        </div>
      );

    case "featureList":
    case "capabilityList":
      return (
        <Block heading={heading}>
          <div className="grid gap-px bg-hair sm:grid-cols-2 border border-hair">
            {items.map((it, i) => (
              <Reveal key={it.id || i} delay={i * 0.05} className="bg-bg p-6">
                <div className="flex items-start gap-3">
                  <span className="mt-1 w-2.5 h-2.5 bg-accent rotate-45 shrink-0" />
                  <div>
                    <h4 className="font-display font-bold uppercase text-fg tracking-wide">{pick({ en: it.titleEn, fr: it.titleFr }, lang)}</h4>
                    {(it.descEn || it.descFr) ? <p className="mt-2 text-mut text-sm leading-relaxed">{pick({ en: it.descEn, fr: it.descFr }, lang)}</p> : null}
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </Block>
      );

    case "statistics":
      return (
        <Block heading={heading}>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-y-10 gap-x-6">
            {items.map((it, i) => (
              <Reveal key={it.id || i} delay={i * 0.06} className="text-center sm:text-left">
                <div className="font-display font-bold text-accent text-[clamp(2.2rem,4vw,3.5rem)] leading-none">
                  <StatCounter value={it.value} suffix={it.suffix} />
                </div>
                <div className="mt-2 font-techmono text-xs uppercase tracking-widest text-mut">{pick({ en: it.labelEn, fr: it.labelFr }, lang)}</div>
              </Reveal>
            ))}
          </div>
        </Block>
      );

    case "quote":
      return (
        <Block>
          <blockquote className="border-l-2 border-accent pl-6 max-w-3xl">
            <p className="font-display text-fg text-2xl md:text-3xl leading-snug">{content}</p>
            {heading ? <footer className="mt-4 font-techmono text-xs uppercase tracking-widest text-mut">— {heading}</footer> : null}
          </blockquote>
        </Block>
      );

    case "applications":
      return (
        <Block heading={heading}>
          <div className="flex flex-wrap gap-3">
            {items.map((it, i) => (
              <span key={it.id || i} className="px-4 py-2 border border-hair text-fg text-sm bg-surface">
                {pick({ en: it.titleEn, fr: it.titleFr }, lang)}
              </span>
            ))}
          </div>
        </Block>
      );

    case "partnerTypes":
    case "compliance":
      return (
        <Block heading={heading}>
          <div className="grid gap-6 sm:grid-cols-2">
            {items.map((it, i) => (
              <Reveal key={it.id || i} delay={i * 0.05} className="glass-card p-6">
                <div className="font-techmono text-accent text-sm">{String(i + 1).padStart(2, "0")}</div>
                <h4 className="mt-3 font-display font-bold uppercase text-fg tracking-wide">{pick({ en: it.titleEn, fr: it.titleFr }, lang)}</h4>
                {(it.descEn || it.descFr) ? <p className="mt-2 text-mut text-sm leading-relaxed">{pick({ en: it.descEn, fr: it.descFr }, lang)}</p> : null}
              </Reveal>
            ))}
          </div>
        </Block>
      );

    case "gallery":
      return (
        <Block heading={heading}>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            {items.map((it, i) => (
              <div key={it.id || i} className="aspect-[3/2] overflow-hidden border border-hair">
                <img src={mediaUrl(it.imageUrl, { w: 600, fmt: "webp", q: 78 })} alt={pick({ en: it.imageAltEn, fr: it.imageAltFr }, lang)} loading="lazy" decoding="async" className="w-full h-full object-cover" />
              </div>
            ))}
          </div>
        </Block>
      );

    case "contactCta":
      return (
        <Block>
          <div className="glass-card p-8 md:p-10 text-center border border-accent/20" data-testid="block-contactcta-inner">
            {heading ? <h3 className="h-display text-fg text-2xl md:text-3xl">{heading}</h3> : null}
            {content ? <div className="mt-4 max-w-2xl mx-auto"><RichText html={content} /></div> : null}
          </div>
        </Block>
      );

    case "relatedServices":
      if (!related.length) return null;
      return (
        <Block heading={heading || (lang === "fr" ? "Capacités connexes" : "Related Capabilities")}>
          <div className="grid gap-6 md:grid-cols-3">
            {related.map((r) => <ServiceCard key={r.id} service={r} testIdPrefix="related-service" />)}
          </div>
        </Block>
      );

    default:
      return null;
  }
}

function Block({ heading, children }) {
  return (
    <div>
      {heading ? <SectionHeading title={heading} /> : null}
      <div className={heading ? "mt-10" : ""}>{children}</div>
    </div>
  );
}
